// **رسالةُ الترحيب عند الدخول** (قرار المالك 22/09/2026: «بمجرد ما يضغط تسجيل دخول تظهر
// له رسالة تفكّره بتسجيل الحضور لو كان نسيه، ورسالة في الأول ترحيبية — السلام عليكم،
// صباح الخير يا فلان، يومك جميل، ويفكّره بالبصمة»).
//
// **مرّةً واحدة في اليوم لا مرّةً في كل فتحة**: الموظف يفتح البرنامج ويقفله ويحدّث الصفحة
// عشرات المرّات، ونافذةٌ تعترضه في كلّ مرّة تصير شيئًا يُغلق بلا قراءة — فتضيع الفائدة
// التي قامت من أجلها. والعلامة في متصفّحه باسمه ويومه: يومٌ جديد = رسالةٌ جديدة.
//
// **ولا تُعرض قبل أن تُعرف الحقيقة**: من كان مسجَّلًا في الحضور تُقرأ حالةُ يومه أوّلًا،
// فلا يُقال «ماتنساش تبصم» لمن بصم من ساعة. ومن ليس في الحضور (الأدمن والشريك) يرى
// الترحيب وحده بلا ذكرٍ لبصمة لا تخصّه.
//
// **و«ليس في الحضور» غير «لم نعرف بعد»**: تسجيلُ الموظف في الحضور يُقرأ من الخادم، وقيمتُه
// قبل الرد `false` (افتراضٌ مغلق في useEnrollment). فلو عُرضت الرسالة في تلك اللحظة ظهرت
// بلا تذكرة البصمة — ثم تُعلَّم «عُرضت اليوم» فلا تعود (قرار المالك 22/09/2026: «نسيت
// تقوله على بصمة الحضور برضه»). فالانتظار هنا شرطٌ: `null` تعني «لسه ما عرفناش».
//
// **ومعها نصيحةُ اليوم** (قرار المالك: «خلّي عندك ١٠٠ نصيحة في المبيعات الخاصة بحجز
// تذاكر الطيران والفنادق… كل يوم بنصيحة مختلفة»): سطرٌ واحد يُقرأ في ثانيتين ويُعمل به
// اليوم. واختيارُه حسابٌ من تاريخ اليوم لا عشوائية — فالكلُّ يقرأ نصيحةً واحدة ويتكلّمون
// فيها، ومن يفتح البرنامج ثلاث مرّات لا يرى ثلاث نصائح. (انظر tips.ts)
//
// **ولا تحبس أحدًا**: تُغلق بضغطة، والبصمة تبقى في الترويسة وفي شاشتها كما كانت. هذه
// تذكرةٌ لا بوّابة.
import { useEffect, useState } from 'react';
import { get, type AttendanceToday, type Me } from '../lib/api.ts';
import { Alert, Modal } from '../components/ui.tsx';
import { cairoTimeAr, cairoToday } from '../lib/format.ts';
import { greetAr } from '../lib/greeting.ts';
import { useTap } from '../attendance/useTap.ts';
import { tipOfDay } from './tips.ts';

/** علامةُ «عُرضت اليوم» — في متصفّح صاحبها وحده، وباسمه كي لا يرثها من يدخل بعده. */
const SEEN_KEY = 'icontravel.welcome';

export function WelcomeDialog({ me, enrolled }: {
  me: Me;
  /** `null` = لم يصل ردُّ الخادم بعد — لا تُعرض الرسالة حتى يُعرف الجواب */
  enrolled: boolean | null;
}) {
  const today = cairoToday();
  const mark = `${me.id}:${today}`;

  const [show, setShow] = useState(false);
  const [att, setAtt] = useState<AttendanceToday | null>(null);
  /**
   * **تعذّرت قراءة حالة اليوم**. والتذكرةُ تُعرض رغم ذلك (قرار المالك 22/09/2026:
   * «برضه نسيت تفكّرهم بالبصمة — وهو بيبدأ يومه فكّره ببصمة الحضور»): كان الصمتُ عند
   * تعذّر القراءة يُسقط التذكرةَ كلَّها، فيمرّ اليومُ بلا بصمة ولا تنبيه. والخطأ في
   * تذكيرِ من بصم أهونُ من سكوتٍ عمّن نسي — ولذلك تُصاغ بلا ادّعاء حالة.
   */
  const [unknown, setUnknown] = useState(false);
  const [closed, setClosed] = useState(false);

  const { busy, error, done, tap } = useTap(async () => {
    try { setAtt(await get<AttendanceToday>('/attendance/today')); } catch { /* التأكيد ظاهرٌ على كل حال */ }
  });

  useEffect(() => {
    let alive = true;
    /** العلامة تُقرأ وتُكتب في محاولةٍ محاطة: التخزين ممنوعٌ في التصفّح الخاص. */
    let seen = '';
    try { seen = localStorage.getItem(SEEN_KEY) ?? ''; } catch { /* بلا تخزين: تُعرض مرّة في الجلسة */ }
    if (seen === mark || enrolled === null) return;

    void (async () => {
      // حالةُ اليوم أوّلًا لمن هو في الحضور — فلا يُذكَّر من بصم بالفعل
      if (enrolled) {
        try { const t = await get<AttendanceToday>('/attendance/today'); if (alive) setAtt(t); }
        catch { if (alive) setUnknown(true); }
      }
      if (!alive) return;
      setShow(true);
      try { localStorage.setItem(SEEN_KEY, mark); } catch { /* تُعرض مرّة في هذه الجلسة وكفى */ }
    })();
    return () => { alive = false; };
  }, [enrolled, mark]);

  if (!show || closed) return null;

  const checkedIn = att?.checkedIn === true;
  const remind = enrolled === true && (unknown || att !== null) && !checkedIn && done === null;

  return (
    <Modal size="sm" title={greetAr(me.fullNameAr)} onClose={() => setClosed(true)}>
      <p className="text-base font-semibold text-ink-900">السلام عليكم ورحمة الله وبركاته</p>
      <p className="mt-1 text-sm text-ink-600">يومك جميل ومبيعاتك موفّقة بإذن الله.</p>

      {/* نصيحةُ اليوم: واحدةٌ للجميع، وتتبدّل بتبدّل اليوم */}
      <div className="mt-3 rounded-xl border border-brand-200 bg-brand-50/70 p-3">
        <div className="text-xs font-bold text-brand-800">💡 نصيحة النهاردة</div>
        <p className="mt-1 text-sm leading-relaxed text-ink-800">{tipOfDay(today)}</p>
      </div>

      {remind && (
        <div className="mt-3 rounded-xl border-2 border-amber-300 bg-amber-50 p-3 text-amber-950">
          <div className="text-sm font-bold">⏰ ماتنساش تسجّل حضورك</div>
          <p className="mt-1 text-xs leading-relaxed">
            البصمة هي اللي بتحفظ حقّك في المرتب والإجازات — سجّلها دلوقتي وخلّيك مرتاح طول اليوم.
            {unknown && ' (لو بصمت خلاص من مكان تاني، اقفل الرسالة دي عادي.)'}
          </p>
        </div>
      )}

      {enrolled === true && checkedIn && att?.firstCheckInAt && (
        <p className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm font-semibold text-emerald-900">
          ✅ حضورك متسجّل الساعة {cairoTimeAr(att.firstCheckInAt)} — شغل موفّق.
        </p>
      )}

      {done && (
        <p className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm font-semibold text-emerald-900">
          ✅ اتسجّل حضورك الساعة {cairoTimeAr(done.at)}.
        </p>
      )}
      {done?.lateNotice && (
        <p className="mt-2 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm font-semibold text-rose-900">⏰ {done.lateNotice}</p>
      )}
      {error && <div className="mt-3"><Alert>{error}</Alert></div>}

      <div className="mt-4 flex flex-wrap gap-2">
        {remind && (
          <button type="button" className="btn-primary px-5 py-2" disabled={busy !== null}
            onClick={() => void tap('CHECK_IN')}>
            {busy !== null ? 'جارٍ التسجيل…' : 'سجّل حضوري دلوقتي'}
          </button>
        )}
        <button type="button" className="btn-ghost px-5 py-2" disabled={busy !== null}
          onClick={() => setClosed(true)}>
          {remind ? 'هسجّل بعدين' : 'يلا نبدأ'}
        </button>
      </div>
    </Modal>
  );
}
